import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { Observable, of, throwError, from } from 'rxjs';
import { map, tap, switchMap, catchError } from 'rxjs/operators';
import {
  AuthTokenResponse,
  AuthTokenInfo,
  LoginCredentials,
  AuthConfig,
  DEFAULT_AUTH_CONFIG,
} from '../models/auth.models';
import {
  buildAuthorizeUrl,
  deriveCodeChallenge,
  generateCodeVerifier,
  generateState,
  parseAuthorizeRedirect,
} from './oauth-pkce';
import { InteractiveAuthProvider } from './interactive-auth.provider';

/**
 * UPS1 self-hosted authentication service.
 * Replaces Auth0 for new apps (Ryn's Roost Mobile is the pilot).
 *
 * Supports two login methods:
 * 1. Active Directory via Entra ID ROPC flow (primary)
 * 2. Local password via Software Security API (fallback for non-AD users)
 *
 * Shared between web and NativeScript apps.
 * Token storage is platform-specific (injected via abstract methods).
 */
@Injectable({ providedIn: 'root' })
export class UpsAuthService {
  private config: AuthConfig = { ...DEFAULT_AUTH_CONFIG };

  readonly isAuthenticated = signal(false);
  readonly currentUser = signal<AuthTokenInfo | null>(null);
  readonly isLoading = signal(false);

  constructor(
    private readonly http: HttpClient,
    private readonly interactive: InteractiveAuthProvider
  ) {}

  configure(config: Partial<AuthConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Login with Active Directory credentials via Entra ID ROPC flow.
   * This calls the Microsoft token endpoint directly, no Auth0 middleman.
   */
  loginWithAd(username: string, password: string): Observable<AuthTokenInfo> {
    this.isLoading.set(true);

    const tokenUrl =
      this.config.tokenEndpointOverride ??
      `https://login.microsoftonline.com/${this.config.tenantId}/oauth2/v2.0/token`;

    const body = new HttpParams()
      .set('client_id', this.config.clientId)
      .set('scope', this.config.scope)
      .set('username', username)
      .set('password', password)
      .set('grant_type', 'password');

    return this.http
      .post<AuthTokenResponse>(tokenUrl, body.toString(), {
        headers: new HttpHeaders({
          'Content-Type': 'application/x-www-form-urlencoded',
        }),
      })
      .pipe(
        map((response) => this.parseTokenResponse(response, username)),
        tap((tokenInfo) => {
          this.currentUser.set(tokenInfo);
          this.isAuthenticated.set(true);
          this.saveTokens(tokenInfo);
        }),
        tap(() => this.isLoading.set(false)),
        catchError((err) => {
          this.isLoading.set(false);
          return throwError(() => err);
        })
      );
  }

  /**
   * Interactive Active Directory sign-in via the system browser (WI #31089).
   *
   * Replaces the ROPC `loginWithAd` for the Entra path: ROPC posts the raw
   * username+password to the token endpoint, which Entra rejects with
   * AADSTS50076 when MFA is required. This flow opens the Microsoft sign-in in
   * the system browser (ASWebAuthenticationSession / Chrome Custom Tabs) where
   * MFA completes, then redeems the returned auth code with PKCE. No password
   * ever touches the app. The refresh token it returns feeds the existing
   * `refresh()` / `tryRestoreSession()` silent-renew machinery unchanged.
   *
   * The browser interaction and crypto come from the injected
   * {@link InteractiveAuthProvider} (NativeScript supplies both; base throws).
   */
  loginWithAdInteractive(): Observable<AuthTokenInfo> {
    const cfg = this.config;
    const redirectUri = cfg.redirectUri;
    if (!redirectUri) {
      return throwError(
        () =>
          new Error('redirectUri is not configured for interactive AD sign-in')
      );
    }
    const authorizeEndpoint =
      cfg.authorizeEndpointOverride ??
      `https://login.microsoftonline.com/${cfg.tenantId}/oauth2/v2.0/authorize`;
    const scopes = cfg.interactiveScopes ?? [
      'openid',
      'profile',
      'offline_access',
      'user.read',
      `api://${cfg.clientId}/Access.All`,
    ];

    this.isLoading.set(true);

    const authorize = (async () => {
      const verifier = generateCodeVerifier((n) =>
        this.interactive.randomBytes(n)
      );
      const state = generateState((n) => this.interactive.randomBytes(n));
      const codeChallenge = await deriveCodeChallenge(verifier, (s) =>
        this.interactive.sha256(s)
      );
      const url = buildAuthorizeUrl({
        authorizeEndpoint,
        clientId: cfg.clientId,
        redirectUri,
        scopes,
        state,
        codeChallenge,
      });
      const redirect = await this.interactive.authorize(url, redirectUri);
      const parsed = parseAuthorizeRedirect(redirect, state);
      // Equality (not truthiness) on the discriminant: the web apps compile
      // without strictNullChecks, where `!parsed.ok` does NOT narrow the
      // union, so `parsed.error` fails to typecheck the first time a web
      // build pulls this lib in. `=== false` narrows under both configs.
      if (parsed.ok === false) {
        throw new Error(parsed.error);
      }
      return { code: parsed.code, verifier };
    })();

    return from(authorize).pipe(
      switchMap(({ code, verifier }) =>
        this.exchangeAuthorizationCode(code, verifier, redirectUri, scopes)
      ),
      tap((tokenInfo) => {
        this.currentUser.set(tokenInfo);
        this.isAuthenticated.set(true);
        this.saveTokens(tokenInfo);
        this.isLoading.set(false);
      }),
      catchError((err) => {
        this.isLoading.set(false);
        return throwError(() => err);
      })
    );
  }

  /** Redeem an authorization code + PKCE verifier for tokens (RFC 7636 §4.5). */
  private exchangeAuthorizationCode(
    code: string,
    codeVerifier: string,
    redirectUri: string,
    scopes: string[]
  ): Observable<AuthTokenInfo> {
    const tokenUrl =
      this.config.tokenEndpointOverride ??
      `https://login.microsoftonline.com/${this.config.tenantId}/oauth2/v2.0/token`;

    const body = new HttpParams()
      .set('client_id', this.config.clientId)
      .set('grant_type', 'authorization_code')
      .set('code', code)
      .set('redirect_uri', redirectUri)
      .set('code_verifier', codeVerifier)
      .set('scope', scopes.join(' '));

    return this.http
      .post<AuthTokenResponse>(tokenUrl, body.toString(), {
        headers: new HttpHeaders({
          'Content-Type': 'application/x-www-form-urlencoded',
        }),
      })
      .pipe(map((response) => this.parseTokenResponse(response, '')));
  }

  /**
   * Login with local credentials (non-AD users).
   * Calls Software Security API which validates against PBKDF2-hashed passwords.
   */
  loginWithPassword(
    username: string,
    password: string
  ): Observable<AuthTokenInfo> {
    this.isLoading.set(true);

    return this.http
      .post<AuthTokenResponse>(
        `${this.config.securityApiBaseUrl}/api/auth/login`,
        { username, password }
      )
      .pipe(
        map((response) => this.parseTokenResponse(response, username)),
        tap((tokenInfo) => {
          this.currentUser.set(tokenInfo);
          this.isAuthenticated.set(true);
          this.saveTokens(tokenInfo);
        }),
        tap(() => this.isLoading.set(false)),
        catchError((err) => {
          this.isLoading.set(false);
          return throwError(() => err);
        })
      );
  }

  /**
   * Unified login that tries AD first, falls back to local.
   */
  login(credentials: LoginCredentials): Observable<AuthTokenInfo> {
    if (credentials.method === 'ad') {
      return this.loginWithAd(credentials.username, credentials.password);
    }
    return this.loginWithPassword(credentials.username, credentials.password);
  }

  /**
   * Refresh the access token using the stored refresh token.
   */
  refresh(): Observable<AuthTokenInfo> {
    const current = this.currentUser();
    if (!current?.refreshToken) {
      return throwError(() => new Error('No refresh token available'));
    }

    const tokenUrl =
      this.config.tokenEndpointOverride ??
      `https://login.microsoftonline.com/${this.config.tenantId}/oauth2/v2.0/token`;

    // No `scope` on refresh: an omitted scope makes Entra reissue the ORIGINAL
    // grant's scopes, which is correct for BOTH login paths, the ROPC login
    // (graph/.default) and the interactive AD login (api://<clientId>/Access.All).
    // Sending this.config.scope here would force the ROPC/Graph scope onto a token
    // that was granted Access.All, silently downgrading or breaking the API token
    // on silent renew after an interactive sign-in (WI #31089, grok N2).
    const body = new HttpParams()
      .set('client_id', this.config.clientId)
      .set('refresh_token', current.refreshToken)
      .set('grant_type', 'refresh_token');

    return this.http
      .post<AuthTokenResponse>(tokenUrl, body.toString(), {
        headers: new HttpHeaders({
          'Content-Type': 'application/x-www-form-urlencoded',
        }),
      })
      .pipe(
        map((response) => this.parseTokenResponse(response, current.email)),
        tap((tokenInfo) => {
          this.currentUser.set(tokenInfo);
          this.saveTokens(tokenInfo);
        })
      );
  }

  /**
   * Check if the current token is still valid (not expired).
   */
  isTokenValid(): boolean {
    const current = this.currentUser();
    if (!current) return false;
    return Date.now() < current.expiresAt - 60000; // 1 min buffer
  }

  /**
   * Get the current access token for API calls.
   * Auto-refreshes if expired.
   */
  getAccessToken(): Observable<string> {
    if (this.isTokenValid()) {
      return of(this.currentUser()!.accessToken);
    }

    return this.refresh().pipe(map((info) => info.accessToken));
  }

  /**
   * Logout: clear tokens and state.
   */
  logout(): void {
    this.currentUser.set(null);
    this.isAuthenticated.set(false);
    this.clearTokens();
  }

  /**
   * Try to restore session from saved tokens on app startup.
   */
  tryRestoreSession(): Observable<boolean> {
    const saved = this.loadTokens();
    if (!saved) return of(false);

    this.currentUser.set(saved);

    if (this.isTokenValid()) {
      this.isAuthenticated.set(true);
      return of(true);
    }

    // Token expired, try refresh
    if (saved.refreshToken) {
      return this.refresh().pipe(
        map(() => {
          this.isAuthenticated.set(true);
          return true;
        }),
        catchError(() => {
          this.logout();
          return of(false);
        })
      );
    }

    this.logout();
    return of(false);
  }

  // --- Token storage (platform-agnostic, uses localStorage by default) ---
  // Override these in platform-specific subclasses for secure storage

  protected saveTokens(info: AuthTokenInfo): void {
    try {
      localStorage.setItem('ups_auth_tokens', JSON.stringify(info));
    } catch {
      // localStorage may not be available on all platforms
    }
  }

  protected loadTokens(): AuthTokenInfo | null {
    try {
      const stored = localStorage.getItem('ups_auth_tokens');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }

  protected clearTokens(): void {
    try {
      localStorage.removeItem('ups_auth_tokens');
    } catch {
      // ignore
    }
  }

  private parseTokenResponse(
    response: AuthTokenResponse,
    username: string
  ): AuthTokenInfo {
    // Decode JWT payload to extract claims
    const payload = this.decodeJwtPayload(response.access_token);

    return {
      accessToken: response.access_token,
      refreshToken: response.refresh_token ?? null,
      expiresAt: Date.now() + response.expires_in * 1000,
      userId:
        payload['http://security.universalplant.com/user-id'] ??
        payload['oid'] ??
        '',
      email:
        payload[
          'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'
        ] ??
        payload['preferred_username'] ??
        payload['upn'] ??
        username,
      displayName:
        payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'] ??
        payload['name'] ??
        username,
      adUserName: payload['upn'] ?? payload['preferred_username'] ?? null,
    };
  }

  private decodeJwtPayload(token: string): Record<string, string> {
    try {
      const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      // Bug 35625: a JWT segment is unpadded base64url. Browsers' atob pads
      // leniently; NativeScript's returns '' for a length that is not a multiple
      // of 4, which left every claim (userId included) empty on mobile.
      const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
      return JSON.parse(atob(padded));
    } catch {
      return {};
    }
  }
}
